import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import UserLevel from '../components/UserLevel';
import UserBadges, { Badge } from '../components/UserBadges';
import Post, { PostProps } from '../components/Post';
import FollowButton from '../components/FollowButton';
import BadgeDebugTool from '../components/BadgeDebugTool';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Edit, Settings, Camera, Link as LinkIcon, MapPin, Calendar, KeyRound } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { 
  getProfile,
  updateProfile, 
  getUserBadges, 
  getUserPosts, 
  isFollowing as checkIsFollowing, 
  checkUsernameExists, 
  uploadProfileImage,
  updateProfileBanner, 
  updateProfileBannerDirect,
  updateProfileBannerFallback,
  updateProfileAvatar
} from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SetPasswordForm from '@/components/auth/SetPasswordForm';
import { registerExperienceListener } from '@/lib/experienceManager';

// Sample user data - in a real app this would come from an API or database
const users = {
  '123': {
    id: '123',
    name: 'Tamika Fillcot',
    username: '@tamikafillcot',
    avatar: '/placeholder.svg',
    cover: 'https://images.unsplash.com/photo-1586672806791-3a67d24186c0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    bio: 'Digital designer & developer based in New York. Passionate about creating beautiful interfaces and experiences.',
    level: 28,
    experience: 2800,
    nextLevelExperience: 3500,
    posts: 15500,
    followers: 5200000,
    following: 1200,
    website: 'https://tamikafillcot.design',
    joinedDate: 'September 2019',
    location: 'New York, USA',
    isCurrentUser: true,
    isFollowing: false,
  },
  '456': {
    id: '456',
    name: 'Jamie Lee',
    username: '@jamielee',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
    cover: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    bio: 'Music producer & DJ. Creating beats that make you move.',
    level: 32,
    experience: 3200,
    nextLevelExperience: 4000,
    posts: 8750,
    followers: 1800000,
    following: 650,
    website: 'https://jamielee.music',
    joinedDate: 'March 2020',
    location: 'Los Angeles, USA',
    isCurrentUser: false,
    isFollowing: true,
  },
  '789': {
    id: '789',
    name: 'Marcus Chen',
    username: '@marcuschen',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
    cover: 'https://images.unsplash.com/photo-1579546929662-711aa81148cf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    bio: 'Guitar player & songwriter. Sharing my musical journey.',
    level: 19,
    experience: 1900,
    nextLevelExperience: 2500,
    posts: 3200,
    followers: 450000,
    following: 210,
    website: 'https://marcuschen.music',
    joinedDate: 'January 2021',
    location: 'Austin, USA',
    isCurrentUser: false,
    isFollowing: false,
  }
};

// Function to generate sample posts based on user
const generateUserPosts = (user: any): PostProps[] => {
  return [
    {
      id: '1',
      user: {
        id: '101',
        name: 'Jane Doe',
        username: '@janedoe',
        avatar_url: '/placeholder.svg',
        level: 5,
      },
      content: 'Just launched my new creative project! Check it out and let me know what you think.',
      imageUrl: 'https://images.unsplash.com/photo-1517292987719-0369a794ec0f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      linkUrl: user.website,
      createdAt: '2025-04-02T14:22:00Z',
      likes: 3540,
      upvotes: 876,
      comments: 210,
      shares: 124,
      tags: ['#creative', '#project', '#design'],
    },
    {
      id: '2',
      user: {
        id: '102',
        name: 'John Smith',
        username: '@johnsmith',
        avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
        level: 3,
      },
      content: 'Working on something new! Here\'s a sneak peek. More coming soon...',
      imageUrl: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      linkUrl: null,
      createdAt: '2025-03-28T09:45:00Z',
      likes: 2890,
      upvotes: 721,
      comments: 145,
      shares: 98,
      tags: ['#wip', '#creative', '#newproject'],
    },
    {
      id: '3',
      user: {
        id: '103',
        name: 'Alex Johnson',
        username: '@alexjohnson',
        avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80',
        level: 7,
      },
      content: 'Guitar player & songwriter. Sharing my musical journey.',
      imageUrl: 'https://images.unsplash.com/photo-1579546929662-711aa81148cf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
      linkUrl: 'https://marcuschen.music',
      createdAt: '2025-04-02T14:22:00Z',
      likes: 3540,
      upvotes: 876,
      comments: 210,
      shares: 124,
      tags: ['#guitar', '#songwriter', '#music'],
    },
  ];
};

// Convert Supabase profile to UI profile format
const convertProfileForUI = (dbProfile: any, isCurrentUser: boolean, isFollowing: boolean = false) => {
  if (!dbProfile) return null;
  
  // Calculate the next level experience required
  const level = dbProfile.level || 1;
  const nextLevelExperience = level * 1000;
  
  return {
    id: dbProfile.id,
    name: dbProfile.display_name || dbProfile.username || 'User',
    username: `@${dbProfile.username || ''}`,
    avatar_url: dbProfile.avatar_url || '/placeholder.svg',
    cover: dbProfile.banner_url || 'https://images.unsplash.com/photo-1586672806791-3a67d24186c0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    bio: dbProfile.bio || '',
    level: level,
    experience: dbProfile.experience || 0,
    nextLevelExperience: nextLevelExperience, // Calculated based on level
    posts: dbProfile.post_count || 0,
    followers: dbProfile.follower_count || 0,
    following: dbProfile.following_count || 0,
    website: dbProfile.website || '',
    joinedDate: new Date(dbProfile.created_at || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    location: dbProfile.location || '',
    isCurrentUser,
    isFollowing // Use the provided value instead of hardcoding false
  };
};

// Function to format post data from the database to match the PostProps interface
const formatDatabasePosts = (fetchedPosts: any[]): PostProps[] => {
  if (!fetchedPosts || fetchedPosts.length === 0) return [];
  
  return fetchedPosts.map(post => {
    // Process image URLs
    const imageUrls = post.post_images?.map((img: any) => {
      let url = img.image_url;
      if (!url) return null;
            
      try {
        // Handle bucket/path format (primary storage format)
        if (url.includes('/') && !url.startsWith('http')) {
          return `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${url}`;
        }
        
        // Already a fully qualified URL, just ensure it's HTTPS
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url.replace(/^http:/, 'https:');
        }
        
        // Relative URL path, add origin
        if (url.startsWith('/')) {
          return `${window.location.origin}${url}`;
        }
        
        // Unknown format, try prefixing with Supabase storage URL
        return `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${url}`;
      } catch (error) {
        console.error(`Error processing image URL: ${url}`, error);
        return null;
      }
    }).filter(Boolean) || [];
    
    // Process tools data
    const tools = post.post_tools?.map((tool: any) => tool.tool) || [];
    console.log(`Processing tools for post ${post.id}:`, tools);
    
    return {
      id: post.id,
      user: {
        id: String(post.user_id),
        name: post.profiles?.display_name || 'User',
        username: `@${post.profiles?.username || 'user'}`,
        avatar_url: post.profiles?.avatar_url || '/placeholder.svg',
        level: post.profiles?.level || 1,
        experience: post.profiles?.experience || 0,
      },
      content: post.content,
      images: post.post_images?.length > 0 ? post.post_images.map((img: any) => ({ 
        image_url: img.image_url 
      })) : [],
      link_url: post.link_url,
      created_at: post.created_at,
      likes: post.likes_count || 0,
      upvotes: post.upvotes_count || 0,
      comments: post.comments_count || 0,
      shares: post.shares_count || 0,
      openForCollaboration: post.open_for_collaboration,
      tags: [], // Would need to fetch separately
      tools: tools,
    };
  });
};

const Profile = () => {
  const { userId } = useParams();
  const { toast } = useToast();
  const { user, profile: authProfile, loading: authLoading, refreshProfile } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [originalProfileData, setOriginalProfileData] = useState<any>(null);
  const [userPosts, setUserPosts] = useState<PostProps[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [isProfileImageDialogOpen, setIsProfileImageDialogOpen] = useState(false);
  const [isBannerImageDialogOpen, setIsBannerImageDialogOpen] = useState(false);
  const [selectedProfileImage, setSelectedProfileImage] = useState<File | null>(null);
  const [selectedBannerImage, setSelectedBannerImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [bannerImagePreview, setBannerImagePreview] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  
  // Handle post deletion
  const handlePostDeleted = (postId: string | number) => {
    console.log(`Removing deleted post ${postId} from profile`);
    // Remove the deleted post from the userPosts array
    setUserPosts(prevPosts => prevPosts.filter(post => post.id.toString() !== postId.toString()));
    
    // Update the post count in profile data
    if (profileData) {
      setProfileData({
        ...profileData,
        posts: profileData.posts > 0 ? profileData.posts - 1 : 0
      });
    }
  };
  
  // Listen for experience updates
  useEffect(() => {
    if (!userId && user?.id && profileData?.isCurrentUser) {
      console.log('Setting up experience listener for current user profile');
      
      // Register for experience updates
      const unregister = registerExperienceListener((updatedUserId, experience, level) => {
        if (updatedUserId === user.id) {
          console.log(`Experience update received: user=${updatedUserId}, exp=${experience}, level=${level}`);
          
          // Calculate the next level experience required
          const nextLevelExperience = level * 1000;
          
          // Update profile data with new experience and level
          setProfileData(prevData => {
            if (!prevData) return null;
            
            return {
              ...prevData,
              experience: experience,
              level: level,
              nextLevelExperience: nextLevelExperience
            };
          });
          
          // Also update originalProfileData to maintain sync when editing
          setOriginalProfileData(prevData => {
            if (!prevData) return null;
            
            return {
              ...prevData,
              experience: experience,
              level: level,
              nextLevelExperience: nextLevelExperience
            };
          });
        }
      });
      
      return unregister;
    }
  }, [userId, user?.id, profileData?.isCurrentUser]);
  
  // Determine which profile to load
  useEffect(() => {
    const loadProfile = async () => {
      setIsLoading(true);
      const profileIdToLoad = userId || user?.id;
      
      if (!profileIdToLoad) {
        console.log('No user ID available to load profile.');
        setIsLoading(false);
        setProfileData(null);
        return;
      }
      
      console.log(`Loading profile for ID: ${profileIdToLoad}`);
      
      try {
        const dbProfile = await getProfile(profileIdToLoad);
        
        if (dbProfile) {
          const isCurrentUser = user?.id === dbProfile.id;
          let followingStatus = false;
          if (user && !isCurrentUser) {
            followingStatus = await checkIsFollowing(user.id, profileIdToLoad);
          }
          const uiProfile = convertProfileForUI(dbProfile, isCurrentUser, followingStatus);
          setProfileData(uiProfile);
          setOriginalProfileData(uiProfile);
          setIsFollowing(followingStatus);
          
          // Fetch user's posts and badges
          const posts = await getUserPosts(profileIdToLoad);
          const formattedPosts = formatDatabasePosts(posts);
          setUserPosts(formattedPosts);
          
          const userBadges = await getUserBadges(profileIdToLoad);
          setBadges(userBadges);
        } else {
          setProfileData(null);
          setOriginalProfileData(null);
          setUserPosts([]);
          setBadges([]);
        }
      } catch (error) {
        console.error('Error loading profile page data:', error);
        toast({ title: "Error", description: "Could not load profile data.", variant: "destructive" });
        setProfileData(null); 
        setOriginalProfileData(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading) {
      loadProfile();
    }
    
  }, [userId, user, authLoading]);
  
  useEffect(() => {
    // Check if the logged-in user is a Google user who needs to set a password
    const checkGoogleAccount = async () => {
      if (profileData?.isCurrentUser && user) {
        try {
          // Get the user's identities from the auth context
          const identities = user.identities || [];
          // Check if user has Google auth but no password set
          const hasGoogleAuth = identities.some(id => id.provider === 'google');
          const hasPasswordAuth = identities.some(id => id.provider === 'email');
          
          setNeedsPassword(hasGoogleAuth && !hasPasswordAuth);
        } catch (error) {
          console.error('Error checking if user needs password:', error);
        }
      }
    };
    
    checkGoogleAccount();
  }, [profileData?.isCurrentUser, user]);
  
  const profileForm = useForm({
    defaultValues: {
      profileImage: ""
    }
  });

  const bannerForm = useForm({
    defaultValues: {
      bannerImage: ""
    }
  });
  
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfileData((prevData: any) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    if (originalProfileData) {
      setProfileData(originalProfileData);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setProfileData(originalProfileData);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !profileData || !originalProfileData) return;
    
    setIsSaving(true);
    console.log("Profile data before save attempt:", profileData);

    const currentUsername = profileData.username.replace('@', '');
    const originalUsername = originalProfileData.username.replace('@', '');
    
    if (currentUsername !== originalUsername) {
      console.log(`Username changed from "${originalUsername}" to "${currentUsername}". Checking availability...`);
      const usernameExists = await checkUsernameExists(currentUsername, user.id);
      if (usernameExists) {
        toast({
          title: "Username Taken",
          description: `The username "${currentUsername}" is already taken. Please choose another.`,
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }
      console.log(`Username "${currentUsername}" is available.`);
    }

    const updatedProfilePayload: Partial<any> & { id: string } = { id: user.id };
    if (profileData.name !== originalProfileData.name) updatedProfilePayload.display_name = profileData.name;
    if (currentUsername !== originalUsername) updatedProfilePayload.username = currentUsername;
    if (profileData.bio !== originalProfileData.bio) updatedProfilePayload.bio = profileData.bio;
    if (profileData.website !== originalProfileData.website) updatedProfilePayload.website = profileData.website;
    if (profileData.location !== originalProfileData.location) updatedProfilePayload.location = profileData.location;

    if (Object.keys(updatedProfilePayload).length <= 1) {
        toast({ title: "No Changes", description: "You haven't made any changes to save." });
        setIsEditing(false);
        setIsSaving(false);
        return;
    }
    
    console.log("Payload being sent to updateProfile:", updatedProfilePayload);
    
    try {
      const result = await updateProfile(updatedProfilePayload);
      
      if (result) {
        console.log("Update successful, result:", result);
        await refreshProfile(); 
        
        const newlySavedProfile = { ...profileData, username: `@${currentUsername}` };
        setProfileData(newlySavedProfile);
        setOriginalProfileData(newlySavedProfile);
        
        setIsEditing(false);
        toast({
          title: "Profile updated",
          description: "Your profile changes have been saved.",
        });
      } else {
        console.error('updateProfile returned null or false');
      }
    } catch (error) {
      console.error('Unexpected error during handleSaveProfile:', error);
      toast({
        title: "Update failed",
        description: "An unexpected error occurred while saving your profile.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedBannerImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setBannerImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileImageSave = async () => {
    if (!selectedProfileImage || !user) {
      toast({ 
        title: "Error", 
        description: "No profile image selected or user not found.", 
        variant: "destructive" 
      });
      return;
    }
    
    console.log(`[handleProfileImageSave] Attempting to update avatar for user: ${user.id}`);
    
    try {
      // Use our consolidated function to handle the avatar update
      const updatedProfile = await updateProfileAvatar(selectedProfileImage, user.id);
      
      if (updatedProfile) {
        await refreshProfile();
        
        // Update local state with the new avatar URL
        const newAvatarUrl = (updatedProfile as any).avatar_url;
        console.log(`[handleProfileImageSave] Avatar updated successfully. New URL: ${newAvatarUrl}`);
        
        setProfileData((prev: any) => ({ ...prev, avatar_url: newAvatarUrl }));
        setOriginalProfileData((prev: any) => ({ ...prev, avatar_url: newAvatarUrl }));
        
        toast({
          title: "Profile picture updated",
          description: "Your profile picture has been updated successfully."
        });
      } else {
        throw new Error("Failed to update profile picture");
      }
    } catch (error: any) {
      console.error('[handleProfileImageSave] Error saving avatar image:', error);
      
      // Provide more helpful error message
      let errorMessage = "There was a problem updating your profile picture.";
      if (error.message?.includes('row-level security')) {
        errorMessage = "Permission error: You don't have access to update this profile.";
      } else if (error.message?.includes('storage')) {
        errorMessage = "Error storing image: The file couldn't be saved to storage.";
      }
      
      toast({
        title: "Update failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProfileImageDialogOpen(false);
      setSelectedProfileImage(null);
      setProfileImagePreview(null);
    }
  };

  const handleBannerImageSave = async () => {
    if (!selectedBannerImage || !user) {
      toast({ 
        title: "Error", 
        description: "No banner image selected or user not found.", 
        variant: "destructive" 
      });
      return;
    }
    
    setIsUploadingBanner(true);
    console.log(`[handleBannerImageSave] Attempting to update banner for user: ${user.id}`);

    try {
      // Use our consolidated function to handle the banner update
      const updatedProfile = await updateProfileBanner(selectedBannerImage, user.id);
      
      if (updatedProfile) {
        await refreshProfile();
        
        // Update local state with the new banner URL
        const newCoverUrl = (updatedProfile as any).banner_url;
        console.log(`[handleBannerImageSave] Banner updated successfully. New URL: ${newCoverUrl}`);
        
        setProfileData((prev: any) => ({ ...prev, cover: newCoverUrl }));
        setOriginalProfileData((prev: any) => ({ ...prev, cover: newCoverUrl }));
        
        toast({
          title: "Banner updated",
          description: "Your profile banner has been updated successfully."
        });
      } else {
        throw new Error("Failed to update banner image");
      }
    } catch (error: any) {
      console.error('[handleBannerImageSave] Error saving banner image:', error);
      
      // Provide more helpful error message based on error type
      let errorMessage = "There was a problem updating your banner image.";
      if (error.message?.includes('row-level security')) {
        errorMessage = "Permission error: You don't have access to update this profile.";
      } else if (error.message?.includes('storage')) {
        errorMessage = "Error storing image: The file couldn't be saved to storage.";
      }
      
      toast({
        title: "Update failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploadingBanner(false);
      setIsBannerImageDialogOpen(false);
      setSelectedBannerImage(null);
      setBannerImagePreview(null);
    }
  };
  
  // Handle follow status changes
  const handleFollowChange = (following: boolean) => {
    setIsFollowing(following);
    
    // Update the followers count in the UI directly
    if (profileData) {
      setProfileData(prevData => ({
        ...prevData,
        followers: following ? prevData.followers + 1 : prevData.followers - 1
      }));
    }
  };
  
  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin h-8 w-8 border-4 border-vibebops-accent rounded-full border-t-transparent"></div>
        </div>
      </MainLayout>
    );
  }
  
  if (!profileData) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <h2 className="text-xl font-bold mb-2">User not found</h2>
            <p className="text-vibebops-textMuted mb-4">The user you're looking for doesn't exist or has been removed.</p>
            <Link to="/home">
              <Button>Go to Home</Button>
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="mb-16">
        <div className="h-48 md:h-64 bg-vibebops-highlight rounded-b-xl overflow-hidden group relative">
          {profileData.cover && (
            <img src={profileData.cover} alt="Cover" className="w-full h-full object-cover" />
          )}
          {profileData.isCurrentUser && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
              <button
                onClick={() => setIsBannerImageDialogOpen(true)}
                className="p-3 bg-vibebops-background/70 backdrop-blur-sm text-white rounded-full hover:bg-vibebops-background/90 transition-colors"
                aria-label="Change banner image"
              >
                <Camera className="h-6 w-6" />
              </button>
            </div>
          )}
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16 sm:flex sm:items-end sm:space-x-5">
            <div className="relative group flex-shrink-0">
              <Avatar className="h-24 w-24 sm:h-32 sm:w-32 border-4 border-vibebops-background bg-vibebops-card overflow-hidden ring-2 ring-vibebops-highlight">
                <AvatarImage src={profileData.avatar_url} alt={profileData.name} className="w-full h-full object-cover" />
                <AvatarFallback className="bg-vibebops-highlight text-lg sm:text-2xl">
                  {profileData.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              {profileData.isCurrentUser && (
                <button
                  onClick={() => setIsProfileImageDialogOpen(true)}
                  className="absolute inset-0 bg-black/0 group-hover:bg-black/50 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Change profile image"
                >
                  <Camera className="h-7 w-7 text-white" />
                </button>
              )}
            </div>

            <div className="mt-6 sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className="sm:hidden md:block mt-6 min-w-0 flex-1">
                <h1 className="text-2xl font-bold text-vibebops-text truncate">{profileData.name}</h1>
                <p className="text-sm text-vibebops-textMuted truncate">{profileData.username}</p>
              </div>
              <div className="mt-6 flex flex-col justify-stretch space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4">
                {profileData.isCurrentUser ? (
                  <>
                    {isEditing ? (
                      <>
                        <Button variant="outline" onClick={handleCancelEdit} disabled={isSaving}>
                          Cancel
                        </Button>
                        <Button type="submit" form="profileEditForm" disabled={isSaving}>
                          {isSaving ? 'Saving...' : 'Save Profile'}
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={handleEditProfile}>
                        <Edit className="h-4 w-4 mr-2" /> Edit Profile
                      </Button>
                    )}
                    <Button variant="outline" size="icon" asChild>
                      <Link to="/settings">
                        <Settings className="h-4 w-4" />
                        <span className="sr-only">Settings</span>
                      </Link>
                    </Button>
                    {profileData?.isCurrentUser && needsPassword && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => setShowChangePasswordDialog(true)}
                      >
                        <KeyRound size={16} />
                        Set Password
                      </Button>
                    )}
                  </>
                ) : (
                  <FollowButton 
                    userId={profileData.id}
                    initialFollowingState={isFollowing}
                    onFollowChange={handleFollowChange}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="hidden sm:block md:hidden mt-6 min-w-0 flex-1">
             <h1 className="text-2xl font-bold text-vibebops-text truncate">{profileData.name}</h1>
             <p className="text-sm text-vibebops-textMuted truncate">{profileData.username}</p>
          </div>
          
          <div className="flex items-center justify-center sm:justify-start gap-6 my-6 border-t border-b border-vibebops-border py-3">
            <div className="text-center">
              <div className="font-bold text-lg">{formatNumber(profileData.posts)}</div>
              <div className="text-xs text-vibebops-textMuted">Bops</div>
            </div>
            <Link to={`/profile/${profileData.id}/followers`} className="text-center hover:text-vibebops-accent">
              <div className="font-bold text-lg">{formatNumber(profileData.followers)}</div>
              <div className="text-xs text-vibebops-textMuted">Followers</div>
            </Link>
            <Link to={`/profile/${profileData.id}/following`} className="text-center hover:text-vibebops-accent">
              <div className="font-bold text-lg">{formatNumber(profileData.following)}</div>
              <div className="text-xs text-vibebops-textMuted">Following</div>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="md:col-span-2 order-2 md:order-1">
          <Tabs defaultValue="posts" className="w-full">
            <TabsList className="bg-vibebops-card mb-4">
              <TabsTrigger value="posts">Bops</TabsTrigger>
              <TabsTrigger value="badges">Badges</TabsTrigger>
            </TabsList>

            <TabsContent value="posts">
              {userPosts.length > 0 ? (
                <div className="space-y-4">
                  {userPosts.map(post => (
                    <Post 
                      key={post.id} 
                      post={post} 
                      onPostDeleted={handlePostDeleted} 
                    />
                  ))}
                </div>
              ) : (
                <Card className="text-center py-12">
                  <CardContent>
                    <p className="text-vibebops-textMuted">This user hasn't posted anything yet.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="badges">
              <Card>
                <CardHeader>
                  <CardTitle>Badges ({badges.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <UserBadges badges={badges} />
                  
                  {/* Only show badge debug tool when viewing your own profile */}
                  {profileData?.isCurrentUser && (
                    <div className="mt-4">
                      <BadgeDebugTool />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="md:col-span-1 space-y-6 order-1 md:order-2 mb-6 md:mb-0">
          {!isEditing ? (
            <>
              <Card>
                <CardHeader><CardTitle>About</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-vibebops-textMuted whitespace-pre-line">
                    {profileData.bio || 'No bio yet.'}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader><CardTitle>Details</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center text-sm">
                    <LinkIcon className="w-4 h-4 mr-2 text-vibebops-textMuted flex-shrink-0" />
                    {profileData.website ? (
                      <a href={profileData.website} target="_blank" rel="noopener noreferrer" className="text-vibebops-accent hover:underline truncate">
                        {profileData.website.replace(/(^https?:\/\/)/, '')}
                      </a>
                    ) : (
                      <span className="text-vibebops-textMuted">No website</span>
                    )}
                  </div>
                  <div className="flex items-center text-sm">
                    <MapPin className="w-4 h-4 mr-2 text-vibebops-textMuted flex-shrink-0" />
                    <span className="text-vibebops-textMuted">{profileData.location || 'Location not set'}</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 mr-2 text-vibebops-textMuted flex-shrink-0" />
                    <span className="text-vibebops-textMuted">Joined {profileData.joinedDate}</span>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                 <CardHeader className="pb-2 sm:pb-6"><CardTitle>Level & Experience</CardTitle></CardHeader>
                 <CardContent className="px-3 py-2 sm:px-6 sm:py-4">
                    <UserLevel 
                      level={profileData.level} 
                      experience={profileData.experience} 
                      nextLevelExperience={profileData.nextLevelExperience} 
                    />
                 </CardContent>
              </Card>
            </>
          ) : (
            <form id="profileEditForm" onSubmit={handleSaveProfile} className="vibebops-card p-6 space-y-4">
              <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
              <div>
                <Label htmlFor="edit-name" className="text-sm font-medium">Name</Label>
                <Input 
                  id="edit-name"
                  name="name"
                  value={profileData.name || ''}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-username" className="text-sm font-medium">Username</Label>
                <Input 
                  id="edit-username"
                  name="username"
                  value={profileData.username?.replace('@', '') || ''}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setProfileData((prevData: any) => ({
                      ...prevData,
                      [name]: value.replace('@', ''), 
                    }));
                  }}
                  className="mt-1"
                  placeholder="Your unique username"
                />
              </div>
              <div>
                <Label htmlFor="edit-bio" className="text-sm font-medium">Bio</Label>
                <Textarea 
                  id="edit-bio"
                  name="bio"
                  value={profileData.bio || ''}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself..."
                  className="min-h-[100px] mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-website" className="text-sm font-medium">Website</Label>
                <Input 
                  id="edit-website"
                  type="url" 
                  name="website"
                  value={profileData.website || ''}
                  onChange={handleInputChange}
                  placeholder="https://yourwebsite.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-location" className="text-sm font-medium">Location</Label>
                <Input 
                  id="edit-location"
                  type="text"
                  name="location"
                  value={profileData.location || ''}
                  onChange={handleInputChange}
                  placeholder="City, Country"
                  className="mt-1"
                />
              </div>
            </form>
          )}
        </div>
      </div>
      
      <Dialog open={isProfileImageDialogOpen} onOpenChange={setIsProfileImageDialogOpen}>
        <DialogContent className="bg-vibebops-card text-vibebops-text border-vibebops-border">
          <DialogHeader>
            <DialogTitle className="text-vibebops-text">Update Profile Picture</DialogTitle>
            <DialogDescription className="text-vibebops-textMuted">
              Choose a new profile picture to represent you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <Avatar className="h-32 w-32 border-4 border-vibebops-highlight">
                {profileImagePreview ? (
                  <AvatarImage src={profileImagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <AvatarImage src={profileData.avatar_url} alt={profileData.name} className="w-full h-full object-cover" />
                )}
                <AvatarFallback className="bg-vibebops-highlight">
                  {profileData.name?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="profile-image" className="text-sm text-vibebops-textMuted">Select Image</Label>
              <Input 
                id="profile-image" 
                type="file" 
                accept="image/*"
                onChange={handleProfileImageChange}
                className="bg-vibebops-highlight text-vibebops-text border-vibebops-border"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setIsProfileImageDialogOpen(false);
                  setSelectedProfileImage(null);
                  setProfileImagePreview(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleProfileImageSave}
                disabled={!selectedProfileImage}
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isBannerImageDialogOpen} onOpenChange={setIsBannerImageDialogOpen}>
        <DialogContent className="bg-vibebops-card text-vibebops-text border-vibebops-border">
          <DialogHeader>
            <DialogTitle className="text-vibebops-text">Update Banner Image</DialogTitle>
            <DialogDescription className="text-vibebops-textMuted">
              Choose a new banner image for your profile.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="w-full h-32 rounded-lg border-2 border-vibebops-highlight bg-vibebops-highlight overflow-hidden">
                {bannerImagePreview ? (
                  <img src={bannerImagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <img src={profileData.cover} alt="Cover" className="w-full h-full object-cover" />
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="banner-image" className="text-sm text-vibebops-textMuted">Select Image</Label>
              <Input 
                id="banner-image" 
                type="file" 
                accept="image/*"
                onChange={handleBannerImageChange}
                className="bg-vibebops-highlight text-vibebops-text border-vibebops-border"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setIsBannerImageDialogOpen(false);
                  setSelectedBannerImage(null);
                  setBannerImagePreview(null);
                }}
                disabled={isUploadingBanner} // Disable cancel during upload
              >
                Cancel
              </Button>
              <Button 
                onClick={handleBannerImageSave}
                disabled={!selectedBannerImage || isUploadingBanner} // Disable save if no image or uploading
              >
                {isUploadingBanner ? 'Saving...' : 'Save'} {/* Show saving state */}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Set Password for Your Account</DialogTitle>
            <DialogDescription>
              Setting a password allows you to sign in with your email and password in addition to Google.
            </DialogDescription>
          </DialogHeader>
          <SetPasswordForm onComplete={() => setShowChangePasswordDialog(false)} />
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Profile;
